import { Button, ButtonVariant } from "@src/components/button/Button";
import { Loader } from "@src/components/loader/Loader";
import { DateFormatsEnum, formatDate } from "@src/components/utils/dateUtils";
import { clx } from "@src/components/utils/stringUtils";
import { pricingActions, purchasingActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { ListingsItem } from "@src/store/purchasing/getListingsItemsSlice";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import styles from "./ChangeSeats.module.scss";

interface ISeatRow {
	itemId: number;
	listingId: number;
	eventName: string;
	eventDate?: Date;
	eventTime: string;
	venueName: string;
	section: string;
	row: string;
	originalSeat: string;
	seatNumber: string;
	originalIsOddEven: boolean;
	isOddEven: boolean;
}

const toSeatRow = (item: ListingsItem): ISeatRow => ({
	itemId: item.Item_ID,
	listingId: item.Listing_ID,
	eventName: item.Event_Name,
	eventDate: item.Event_Date,
	eventTime: item.Event_Time,
	venueName: item.Venue_Name,
	section: item.Section,
	row: item.Row,
	originalSeat: `${item.Seat_Number ?? ""}`,
	seatNumber: `${item.Seat_Number ?? ""}`,
	originalIsOddEven: !!item.Is_Odd_Even,
	isOddEven: !!item.Is_Odd_Even,
});

export const getInvalidSeatItemIds = (rows: Array<ISeatRow>): Set<number> => {
	const invalid = new Set<number>();

	rows.forEach((row) => {
		const seat = row.seatNumber.trim();
		if (!seat || !/^\d+$/.test(seat)) invalid.add(row.itemId);
	});

	const seen = new Map<string, Array<ISeatRow>>();
	rows.forEach((row) => {
		const key = `${row.listingId}|${row.section}|${row.row}|${row.seatNumber.trim()}`;
		const bucket = seen.get(key) ?? [];
		bucket.push(row);
		seen.set(key, bucket);
	});

	seen.forEach((bucket) => {
		if (bucket.length > 1) bucket.forEach((row) => invalid.add(row.itemId));
	});

	return invalid;
};

interface IChangeSeatsProps {
	listingIds: Array<number>;
	onClose: () => void;
	onSuccess?: () => void;
}

export const ChangeSeats: React.FC<IChangeSeatsProps> = ({ listingIds, onClose, onSuccess }) => {
	const dispatch = useAppDispatch();

	const listingItems = useAppSelector((state) => state.purchasing.listingsItems);
	const saveLoading = useAppSelector((state) => state.pricing.changeSeatNumbers.loading);
	const { hasStageFrontRole, hasForceEditSeats } = useAppSelector((state) => state.settings);
	const isAdmin = useAppSelector((state) => state.settings.isAdmin);

	const [rows, setRows] = useState<Array<ISeatRow>>([]);
	const [submitError, setSubmitError] = useState<string | null>(null);

	const listingIdsKey = listingIds.join(",");

	useEffect(() => {
		if (!listingIdsKey) return;
		dispatch(
			purchasingActions.getListingsItems({ ListingIds: listingIdsKey.split(",").map(Number), skipCache: true }),
		);
	}, [dispatch, listingIdsKey]);

	useEffect(() => {
		setRows((listingItems.data ?? []).map(toSeatRow));
	}, [listingItems.data]);

	const invalidItemIds = useMemo(() => getInvalidSeatItemIds(rows), [rows]);
	const hasChanges = rows.some(
		(row) => row.seatNumber.trim() !== row.originalSeat.trim() || row.isOddEven !== row.originalIsOddEven,
	);
	const canSave = rows.length > 0 && invalidItemIds.size === 0 && hasChanges && !saveLoading;

	const patchRow = (itemId: number, patch: Partial<ISeatRow>) => {
		setRows((prev) => prev.map((row) => (row.itemId === itemId ? { ...row, ...patch } : row)));
	};

	const handleSave = async () => {
		if (!canSave) return;
		setSubmitError(null);

		const payload = rows.map((row) => ({
			ItemId: row.itemId,
			SeatNumber: row.seatNumber.trim(),
			IsOddEven: row.isOddEven,
		}));

		const useForce = hasStageFrontRole || hasForceEditSeats || isAdmin;
		const action = useForce ? pricingActions.forceChangeTicketsSeatNumbers : pricingActions.changeTicketsSeatNumbers;

		try {
			await dispatch(action(payload)).unwrap();

			dispatch(purchasingActions.getListingsItems({ ListingIds: listingIds, skipCache: true }));

			toast.success("Seat numbers updated.");
			onSuccess?.();
			onClose();
		} catch (error) {
			const message = typeof error === "string" ? error : "Failed to update seat numbers. Please try again.";
			setSubmitError(message);
			toast.error(message);
		}
	};

	if (listingItems.loading) {
		return (
			<div className={styles.loaderWrap}>
				<Loader />
			</div>
		);
	}

	if (!rows.length) {
		return <div className={styles.emptyState}>{listingItems.error ?? "No tickets found for this listing."}</div>;
	}

	return (
		<div className={styles.root}>
			<div className={styles.tableScroll}>
				<table className={styles.table}>
					<thead>
						<tr>
							<th>Event</th>
							<th>Date</th>
							<th>Time</th>
							<th>Venue</th>
							<th>Section</th>
							<th>Row</th>
							<th className={styles.seatCell}>Seat</th>
							<th className={styles.oddEvenCell}>Odd/Even</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => {
							const isInvalid = invalidItemIds.has(row.itemId);
							const isChanged = row.seatNumber.trim() !== row.originalSeat.trim();

							return (
								<tr key={row.itemId}>
									<td>{row.eventName}</td>
									<td>{formatDate(row.eventDate, DateFormatsEnum["MM/DD/YYYY"], "")}</td>
									<td>{row.eventTime}</td>
									<td>{row.venueName}</td>
									<td>{row.section}</td>
									<td>{row.row}</td>
									<td className={styles.seatCell}>
										<input
											className={clx({
												[styles.seatInput]: true,
												[styles.seatInputDirty]: isChanged && !isInvalid,
												[styles.seatInputInvalid]: isInvalid,
											})}
											value={row.seatNumber}
											inputMode="numeric"
											title={isInvalid ? "Seat must be a number and unique within the section and row." : undefined}
											onChange={(e) => patchRow(row.itemId, { seatNumber: e.target.value.replace(/\D/g, "") })}
										/>
									</td>
									<td className={styles.oddEvenCell}>
										<input
											type="checkbox"
											checked={row.isOddEven}
											onChange={(e) => patchRow(row.itemId, { isOddEven: e.target.checked })}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{submitError ? <div className={styles.errorBanner}>{submitError}</div> : null}

			<div className={styles.buttons}>
				<Button onClick={onClose} disabled={saveLoading} variant={ButtonVariant.Secondary}>
					Cancel
				</Button>
				<Button onClick={handleSave} isLoading={saveLoading} disabled={!canSave}>
					Save
				</Button>
			</div>
		</div>
	);
};
